import * as React from "react"

import { cn } from "../../cn"
import { Skeleton } from "../Skeleton/Skeleton"
import {
  EDGE_GUTTER_CLASS,
  ESTIMATED_ROW_HEIGHT,
  FLUSH_EDGE_CELLS_CLASS,
  SKELETON_ROW_COUNT,
} from "../Table/TableInternals"

/**
 * Standing-in shape for a table whose data has not arrived, for panes that
 * cannot render the real `Table` yet — a route still resolving its record, or
 * a view whose columns are derived from the data itself.
 *
 * Every band — the header included — is sized from the same
 * `ESTIMATED_ROW_HEIGHT` the real table's virtualizer uses, and carries the
 * same edge gutter and cell padding, down to the real row's flush outer cells,
 * so the rows land where the skeleton put them instead of shifting the pane. The dividers run the full width for the
 * same reason the table's do: they read as the table's own rules rather than
 * as a block inset inside the pane.
 *
 * Where a real `Table` can be mounted, prefer its own `isLoading` rows: those
 * match the actual columns.
 */
export interface TableSkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  rowCount?: number
  columnCount?: number
  /** Renders a header row of shorter bars above the rows. */
  showHeader?: boolean
}

/** Cell bar widths cycle so the block reads as columns of content, not a grid. */
const CELL_WIDTHS = ["w-8", "w-32", "w-16", "w-20", "w-16", "w-24"]

const TableSkeleton = React.forwardRef<HTMLDivElement, TableSkeletonProps>(
  (
    {
      rowCount = SKELETON_ROW_COUNT,
      columnCount = 6,
      showHeader = true,
      className,
      ...props
    },
    ref,
  ) => {
    const columns = Array.from({ length: columnCount })

    const cells = (barClassName: string) =>
      columns.map((_, columnIndex) => (
        <div key={columnIndex} className="flex flex-1 items-center px-3">
          <Skeleton
            className={cn(
              barClassName,
              CELL_WIDTHS[columnIndex % CELL_WIDTHS.length],
            )}
          />
        </div>
      ))

    return (
      <div
        ref={ref}
        role="status"
        aria-label="Loading"
        className={cn("w-full overflow-hidden", className)}
        {...props}
      >
        {showHeader && (
          <div
            className={cn(
              "flex items-center border-b border-coval-border",
              EDGE_GUTTER_CLASS,
              FLUSH_EDGE_CELLS_CLASS,
            )}
            // Every band takes the same height, header included: an even stack
            // of rules reads as a table, where one short band at the top reads
            // as a broken row.
            style={{ height: ESTIMATED_ROW_HEIGHT }}
            aria-hidden="true"
          >
            {cells("h-3")}
          </div>
        )}
        {Array.from({ length: rowCount }).map((_, rowIndex) => (
          <div
            key={rowIndex}
            className={cn(
              "flex items-center border-b border-coval-border",
              EDGE_GUTTER_CLASS,
              FLUSH_EDGE_CELLS_CLASS,
            )}
            // The height the virtualizer gives a real row, so the rows land
            // where these sat rather than reflowing the pane.
            style={{ height: ESTIMATED_ROW_HEIGHT }}
            aria-hidden="true"
          >
            {cells("h-3.5")}
          </div>
        ))}
      </div>
    )
  },
)

TableSkeleton.displayName = "TableSkeleton"

export { TableSkeleton }
